import { useEffect } from 'react';
import noop from 'lodash/noop';

/**
 * https://github.com/WICG/resize-observer/issues/59#issuecomment-408098151
 * We use one module level resize observer instead of a new observer for every
 * component rendered (for performance reasons). Every component will then
 * add/remove "entries" to be observed.
 */
let resizeObserver;
// Protection for IE: we're not concerned with feature parity, but if the
// browser doesn't support it we just throw everything into a black hole
if (window.ResizeObserver) {
  resizeObserver = new ResizeObserver(entries => {
    entries.forEach(entry => {
      // Each time an entry is resized the `onResize` callback attached in the
      // effect below will be triggered. (Callbacks for individual components can
      // be debounced as needed.)
      entry.target.onResize();
    });
  });
} else {
  resizeObserver = { observe: noop, unobserve: noop };
}

/**
 * Hook to attach a callback when an individual element's size changes
 * @param {node} elementRef The element to watch for resizing
 * @param {function} onResize Callback to fire when element updates
 */
const useResizeObserver = (elementRef, onResize, opts) => {
  const { forceRunInSameFrame } = opts ?? {};
  // Attaches/detaches the resize handler
  useEffect(() => {
    // Guard against using effect when ref is undefined
    if (!elementRef.current) return false;

    const elementToWatch = elementRef.current;
    /**
     * We attach the handler to the dom element directly so we can trigger the
     * callback for any given instance from the global observer.
     *
     * By default wraps in a RAF to prevent throwing errors when onResize
     * takes more than one animation frame: https://stackoverflow.com/a/58701523
     * An escape hatch is available, opts.forceRunInSameFrame, for cases where we
     * absolutely need it to be run between layout and paint phases.
     */
    elementToWatch.onResize = forceRunInSameFrame
      ? onResize
      : () => window.requestAnimationFrame(onResize);

    // Attach the element to our resize observer.
    resizeObserver.observe(elementToWatch);
    return () => {
      resizeObserver.unobserve(elementToWatch);
    };
  }, [elementRef, forceRunInSameFrame, onResize]);

  return onResize;
};

export default useResizeObserver;
